import { Box } from '@iheartjane/reefer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';

import type { BannerCarouselProps } from './bannerCarousel.types';
import { CarouselDots } from './carouselDots/carouselDots';
import { CarouselItem } from './carouselItem/carouselItem';
import { CarouselPlayPause } from './carouselPlayPause/carouselPlayPause';

import styles from './bannerCarousel.module.css';

const DEFAULT_AUTO_ADVANCE_MS = 7000;

/** How long a slide must stay active before it counts as viewed */
const VIEW_DEBOUNCE_MS = 250;

/** Wraps past the last slide back to the first one */
const nextIndex = (current: number, total: number) => (current + 1) % total;

/** Wraps before the first slide back to the last one */
const previousIndex = (current: number, total: number) =>
  (current - 1 + total) % total;

export const BannerCarousel = ({
  ariaLabel,
  autoAdvanceMs = DEFAULT_AUTO_ADVANCE_MS,
  className,
  comparisonUrl,
  disableAutoAdvance = false,
  disableKeyboardNavigation = false,
  slides,
  onView,
  ...marginProps
}: BannerCarouselProps) => {
  const totalItems = slides.length;
  const isLooping = totalItems > 1;
  const isAutoAdvancing = isLooping && !disableAutoAdvance;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Slides can disappear while it is selected. So make active the next one
  const activeIndex = Math.min(selectedIndex, Math.max(totalItems - 1, 0));
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastViewedIndexRef = useRef<number | null>(null);
  const viewportRef = useRef<HTMLElement>(null);

  const handleTogglePlayPause = () => setIsPaused((prev) => !prev);

  const startAutoSlide = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (disableAutoAdvance || !isLooping || isPaused) {
      return;
    }

    intervalRef.current = setInterval(() => {
      setSelectedIndex((current) => nextIndex(current, totalItems));
    }, autoAdvanceMs);
  }, [autoAdvanceMs, disableAutoAdvance, isLooping, isPaused, totalItems]);

  const updateIndex = (newIndex: number) => {
    setSelectedIndex(newIndex);
    startAutoSlide();
  };

  const goNext = useCallback(() => {
    if (!isLooping) {
      return;
    }

    setSelectedIndex((current) => nextIndex(current, totalItems));
    startAutoSlide();
  }, [isLooping, startAutoSlide, totalItems]);

  const goPrev = useCallback(() => {
    if (!isLooping) {
      return;
    }

    setSelectedIndex((current) => previousIndex(current, totalItems));
    startAutoSlide();
  }, [isLooping, startAutoSlide, totalItems]);

  useEffect(() => {
    startAutoSlide();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startAutoSlide]);

  useEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport) {
      return;
    }

    const resetScroll = () => {
      viewport.scrollLeft = 0;
    };

    viewport.addEventListener('scroll', resetScroll);

    return () => {
      viewport.removeEventListener('scroll', resetScroll);
    };
  }, []);

  const touchHandlers = useSwipeable({
    onSwipedLeft: () => {
      goNext();
    },
    onSwipedRight: () => {
      goPrev();
    },
    trackMouse: true,
  });

  useEffect(() => {
    if (disableKeyboardNavigation) {
      return;
    }

    const arrowKeyHandlers = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        goPrev();
      }

      if (event.key === 'ArrowRight') {
        goNext();
      }
    };

    window.addEventListener('keyup', arrowKeyHandlers);

    return () => {
      window.removeEventListener('keyup', arrowKeyHandlers);
    };
  }, [disableKeyboardNavigation, goNext, goPrev]);

  // NOTE: This is not a perfect handling of onView for a slide. It reports on index
  // change rather than on real visibility, and it still reports the first slide on
  // mount. This is intentional. We will clean this up later.
  useEffect(() => {
    if (onView == null) return;

    const timeout = setTimeout(() => {
      if (lastViewedIndexRef.current === activeIndex) return;

      lastViewedIndexRef.current = activeIndex;
      onView(slides[activeIndex]);
    }, VIEW_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeout);
    };
  }, [activeIndex, onView, slides]);

  const handleCarouselItemClick = (index: number) => () => {
    updateIndex(index);
  };

  const trackOffset = activeIndex * 100;

  return (
    <Box
      ariaLabel={ariaLabel}
      className={className}
      data-testid="banner-carousel"
      {...marginProps}
    >
      <Box ref={viewportRef} className={styles['carouselViewport']}>
        <div
          className={styles['carouselTrack']}
          style={{ transform: `translateX(-${trackOffset}%)` }}
          {...touchHandlers}
        >
          {slides.map((slide, index) => (
            <CarouselItem
              key={index}
              activeIndex={activeIndex}
              comparisonUrl={comparisonUrl}
              index={index}
              onClick={handleCarouselItemClick(index)}
              slide={slide}
            />
          ))}
        </div>

        {isAutoAdvancing && (
          <CarouselPlayPause
            isPaused={isPaused}
            onToggle={handleTogglePlayPause}
          />
        )}
      </Box>

      <CarouselDots
        activeIndex={activeIndex}
        autoAdvanceMs={disableAutoAdvance ? undefined : autoAdvanceMs}
        count={totalItems}
        isPaused={isPaused}
        onSelect={updateIndex}
      />
    </Box>
  );
};
